// action/whatsapp-msg.tsx

import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown, Edit2 } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel } from "./card-handler";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractVariableSlots(content: string): number[] {
  const found = new Set<number>();
  for (const m of content.matchAll(/\{\{(\d+)\}\}/g)) {
    found.add(Number(m[1]));
  }
  return Array.from(found).sort((a, b) => a - b);
}

function buildFinalMessage(content: string, variables: Record<number, string>): string {
  return content.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const value = variables[Number(n)];
    return value ? value.replace(/^{{/, "").replace(/}}$/, "") : `{{${n}}}`;
  });
}

// Default common lead field mappings
const DEFAULT_VARIABLE_MAPPINGS: Record<number, string> = {
  1: "{{first_name}}",
  2: "{{last_name}}",
  3: "{{email}}",
  4: "{{phone}}",
  5: "{{source}}",
};

const KNOWN_LEAD_FIELDS = new Set([
  "first_name", "last_name", "name", "email", "phone", "source",
  "lead_status", "lead_score", "tags",
]);

interface WhatsAppMessageActionCardProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const WhatsAppMessageActionCard: React.FC<WhatsAppMessageActionCardProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

  const whatsappTemplates = templates.filter(
    (t) => t.channel?.toLowerCase() === "whatsapp" || !t.channel
  );

  // Robust template resolution (handles templateId, templateName, template_name, etc.)
  const selectedTemplate: TemplateModule | null = useMemo(() => {
    const id = config.templateId ?? config.template_id ?? "";
    const name = config.templateName ?? config.template ?? config.template_name ?? "";

    return whatsappTemplates.find((t) =>
      (id && t.id === id) || (name && t.template_name === name)
    ) ?? null;
  }, [whatsappTemplates, config.templateId, config.template_id, config.templateName, config.template, config.template_name]);

  const variableSlots = selectedTemplate
    ? extractVariableSlots(selectedTemplate.content ?? "")
    : [];

  // Fallback button map — Meta has approved these exact buttons on
  // each of these templates (verified 06-29 against the relaybridge
  // catalog). Used when the CRM /templates/ endpoint omits
  // quick_reply_buttons. Without this, picking appointment_reminder
  // or appointment_reschedule renders no buttons at all even though
  // they show on the customer's real WhatsApp. (Joseph 06-30 bug:
  // "the yes no rescdule is not [showing]"; "the tomorow next week
  // this wek for the template add it back".)
  const FALLBACK_BUTTONS_BY_TPL: Record<string, { id: string; label: string }[]> = {
    appointment_reminder: [
      { id: "btn_yes",        label: "YES" },
      { id: "btn_no",         label: "NO" },
      { id: "btn_reschedule", label: "Reschedule" },
    ],
    appointment_reschedule: [
      { id: "btn_tomorrow",  label: "Tomorrow" },
      { id: "btn_this_week", label: "This week" },
      { id: "btn_next_week", label: "Next week" },
    ],
    appointment_cancel_reason: [
      { id: "btn_diff_slot",   label: "Different time slot" },
      { id: "btn_not_interest", label: "Not interested" },
      { id: "btn_need_help",   label: "Need assistance" },
    ],
    appointment_missed: [
      { id: "btn_reschedule", label: "Reschedule" },
      { id: "btn_cancel",     label: "Cancel" },
    ],
    // Feedback Collection preset entry template.
    // Joseph 07-01: "the feedback card should be able to caption
    // Good/Average/Poor in the actions card ... then if its poor its
    // assign them to agent". Now the operator sees the 3 branch
    // radios on the Send Message card and can pick which branch to
    // configure. The actual routing lives in the backend dispatcher
    // (workflows.py _dispatch_feedback_collection_reply) which fires
    // on purpose="feedback_collection".
    feedback_request: [
      { id: "btn_good",    label: "Good" },
      { id: "btn_average", label: "Average" },
      { id: "btn_poor",    label: "Poor" },
    ],
  };
  const effectiveButtons = useMemo(() => {
    if (selectedTemplate?.quick_reply_buttons?.length) {
      return selectedTemplate.quick_reply_buttons as { id?: string; label: string }[];
    }
    if (selectedTemplate?.template_name && FALLBACK_BUTTONS_BY_TPL[selectedTemplate.template_name]) {
      return FALLBACK_BUTTONS_BY_TPL[selectedTemplate.template_name];
    }
    return [];
  }, [selectedTemplate?.id, selectedTemplate?.template_name, selectedTemplate?.quick_reply_buttons]);

  // Local state for variables (syncs with saved config)
  const [variables, setVariables] = useState<Record<number, string>>(config.variables ?? {});

  // ── Initialize / Backfill variables when template is detected ─────────────
  useEffect(() => {
    if (!selectedTemplate) {
      setVariables({});
      return;
    }

    const slots = extractVariableSlots(selectedTemplate.content ?? "");
    const savedVars = config.variables ?? {};
    const newVars: Record<number, string> = { ...savedVars };

    slots.forEach((slot) => {
      if (!newVars[slot] || newVars[slot] === "") {
        newVars[slot] = DEFAULT_VARIABLE_MAPPINGS[slot] || `{{${slot}}}`;
      }
    });

    setVariables(newVars);

    // Push back to parent if anything changed
    if (JSON.stringify(newVars) !== JSON.stringify(savedVars)) {
      onUpdate({ ...config, variables: newVars });
    }
  }, [selectedTemplate?.id, config.variables]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const setVariableValue = (index: number, value: string) => {
    const nextVars = { ...variables, [index]: value };
    setVariables(nextVars);

    const finalMessage = selectedTemplate
      ? buildFinalMessage(selectedTemplate.content ?? "", nextVars)
      : "";

    onUpdate({
      ...config,
      variables: nextVars,
      message: finalMessage,
    });
  };

  const handleTemplateChange = (templateId: string) => {
    const tpl = whatsappTemplates.find((t) => t.id === templateId) ?? null;
    const slots = tpl ? extractVariableSlots(tpl.content ?? "") : [];

    const newVariables: Record<number, string> = {};
    slots.forEach((slotIndex) => {
      newVariables[slotIndex] = DEFAULT_VARIABLE_MAPPINGS[slotIndex] || `{{${slotIndex}}}`;
    });

    const finalMessage = tpl?.content ?? "";

    // If the template has a media header, propagate it to media_url / media_type
    // so the celery send_whatsapp task attaches the required header component
    // when calling Meta. Without this, Meta rejects with 132000 (missing header).
    const headerType = (tpl?.header_type ?? "").toLowerCase().trim();
    const isMediaHeader = ["image", "video", "document", "audio"].includes(headerType);

    onUpdate({
      ...config,
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      template: tpl?.template_name ?? "",
      language: tpl?.language ?? "en_US",
      channel: tpl?.channel ?? "WhatsApp",
      waba_id: tpl?.waba_id ?? "",
      category: tpl?.category ?? "",
      wa_template_id: tpl?.wa_template_id ?? "",
      variables: newVariables,
      message: finalMessage,
      media_url: isMediaHeader ? (tpl?.header_content ?? "") : null,
      media_type: isMediaHeader ? headerType : null,
    });
  };

  const getDisplayValue = (value: string): string => {
    if (!value) return "";
    return value.replace(/^{{(.*)}}$/, "$1");
  };

  return (
    <CardShell
      title="Send Message"
      description="Select and send a message template to customers"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        {/* Template Picker */}
        <div className="mb-3">
          <FieldLabel>Select Template</FieldLabel>
          <div className="relative">
            <select
              value={selectedTemplate?.id ?? config.templateId ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">— choose template —</option>
              {whatsappTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-2 top-2.5 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {selectedTemplate && (
          <>
            {/* Header Image */}
            {selectedTemplate.header_type === "image" && selectedTemplate.header_content && (
              <div className="w-full h-24 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden mb-3">
                <img
                  src={selectedTemplate.header_content}
                  alt="Template header"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              </div>
            )}

            {/* Message Body */}
            <div className="mb-3">
              <FieldLabel>Message Body</FieldLabel>
              <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 bg-gray-50 dark:bg-gray-800 whitespace-pre-line leading-relaxed min-h-[60px]">
                {selectedTemplate.content || "No body text."}
              </div>
            </div>

            {/* Variable Mapping */}
            {variableSlots.length > 0 && (
              <div className="mb-3">
                <FieldLabel>Variable Mapping</FieldLabel>
                <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">Map each placeholder to a lead field</p>
                <div className="space-y-2">
                  {variableSlots.map((idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs font-mono font-medium text-indigo-600 bg-indigo-50 rounded px-1.5 py-1 flex-shrink-0 w-10 text-center">
                        {`{{${idx}}}`}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600 text-xs flex-shrink-0">→</span>
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={getDisplayValue(variables[idx] || "")}
                          onChange={(e) => {
                            const newValue = e.target.value.trim();
                            let storedValue = "";
                            if (newValue) {
                              storedValue = KNOWN_LEAD_FIELDS.has(newValue)
                                ? `{{${newValue}}}`
                                : newValue;
                            }
                            setVariableValue(idx, storedValue);
                          }}
                          placeholder={`{{${idx}}}`}
                          className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                        />
                        <Edit2 size={12} className="absolute right-2 top-2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Live Preview */}
            {variableSlots.length > 0 && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-2 mb-3">
                <p className="text-[10px] font-medium text-indigo-500 mb-1">Live Preview</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                  {buildFinalMessage(selectedTemplate.content ?? "", variables)}
                </p>
              </div>
            )}

            {/* Customer-facing quick-reply buttons (preview of what the
                customer sees on their WhatsApp). Rendered when the chosen
                template has BUTTONS QUICK_REPLY components. */}
            {effectiveButtons.length ? (
              <div className="mb-3">
                <FieldLabel>Customer sees</FieldLabel>
                <div className="space-y-1.5 mt-1.5">
                  {effectiveButtons.map((btn: any) => (
                    <div
                      key={btn.id ?? btn.label}
                      className="w-full text-center text-xs text-indigo-500 bg-gray-50 dark:bg-gray-800 rounded-lg py-2"
                    >
                      {btn.label}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Operator branch selectors — radio per quick-reply. Clicking
                one auto-spawns an inline preview card below showing what
                gets sent for that branch (matches Joseph's June 17 design:
                the canvas changes based on which radio is selected). */}
            {effectiveButtons.length ? (
              <div className="mb-3">
                <FieldLabel>Branch to configure</FieldLabel>
                <div className="space-y-1.5 mt-1.5">
                  {effectiveButtons.map((btn: any) => {
                    const id = btn.id ?? btn.label;
                    const active = config._selectedBranch === btn.label;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          // Branch → next-template mapping. Radios are
                          // single-select, so only the picked branch is
                          // in _selectedBranch at a time (that's what
                          // Joseph asked for: "i can only select one
                          // condition ... user picks one of the three").
                          //   Appointment bot:
                          //     Yes        → assigns to agent
                          //     No         → appointment_cancelled
                          //     Reschedule → appointment_reschedule
                          //   Feedback Collection bot:
                          //     Good       → feedback_thanks_good
                          //     Average    → feedback_suggestions
                          //     Poor       → assigns to agent
                          const label = (btn.label || "").toLowerCase();
                          const branches = { ...(config._branches ?? {}) };
                          if (label === "yes") {
                            branches[id] = { template: null, action: "assign_agent" };
                          } else if (label === "no") {
                            branches[id] = { template: "appointment_cancelled", action: "send" };
                          } else if (label === "reschedule") {
                            branches[id] = { template: "appointment_reschedule", action: "send" };
                          } else if (label === "good") {
                            branches[id] = { template: "feedback_thanks_good", action: "send" };
                          } else if (label === "average") {
                            branches[id] = { template: "feedback_suggestions", action: "send" };
                          } else if (label === "poor") {
                            branches[id] = { template: null, action: "assign_agent" };
                          } else if (!branches[id]) {
                            branches[id] = { template: "", action: "send" };
                          }
                          onUpdate({ ...config, _selectedBranch: btn.label, _branches: branches });
                        }}
                        className={`w-full flex items-center justify-between rounded-lg py-2 px-3 text-xs transition-colors ${active
                            ? "bg-indigo-50 text-indigo-700"
                            : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                      >
                        <span>{btn.label}</span>
                        <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${active ? "border-indigo-500" : "border-gray-300 dark:border-gray-600"
                          }`}>
                          {active && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Auto-spawn branch preview — shows the follow-up card that
                would be sent for the currently selected reply button.
                Mirrors the design screenshots: clicking a radio reveals the
                next card with the auto-picked template. */}
            {effectiveButtons.length && config._selectedBranch ? (() => {
              const btn = effectiveButtons.find(
                (b: any) => b.label === config._selectedBranch
              );
              if (!btn) return null;

              const id = btn.id ?? btn.label;
              if (!id) return null;  // Guard against undefined

              const branch = config._branches?.[id];
              if (!branch) return null;

              if (branch.action === "assign_agent") {
                return (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 mb-1">
                    <p className="text-[10px] font-medium text-indigo-500 mb-0.5">
                      When customer clicks "{btn.label}"
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      → Assigns directly to agent (no follow-up message)
                    </p>
                  </div>
                );
              }

              const branchTpl = whatsappTemplates.find(
                (t) => t.template_name === branch.template
              );
              return (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 mb-1">
                  <p className="text-[10px] font-medium text-indigo-500 mb-1.5">
                    When customer clicks "{btn.label}" → Send Message
                  </p>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                      Template: <span className="font-medium">{branch.template || "—"}</span>
                    </span>
                    {!branchTpl && branch.template && (
                      <span className="text-[10px] text-amber-600 ml-1">
                        (pending Meta approval)
                      </span>
                    )}
                  </div>
                  {branchTpl ? (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-pre-line line-clamp-3 bg-gray-50 dark:bg-gray-800 rounded p-2">
                      {branchTpl.content?.slice(0, 200) || "(no body)"}
                    </div>
                  ) : branch.template ? (
                    <div className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 italic bg-gray-50 dark:bg-gray-800 rounded p-2">
                      Template body previews here once Meta approves "{branch.template}".
                    </div>
                  ) : null}
                </div>
              );
            })() : null}
          </>
        )}
    </CardShell>
  );
};